/**
 * Format utilities — date / activity-day / grouping helpers.
 *
 * Phase 2 modularization Sprint 4 (2026-05-07).
 */
(function (global) {
    'use strict';

    /**
     * Дата активности по дню тура (1-indexed).
     * @param {string} tourStart - 'YYYY-MM-DD'
     * @param {number} tourDay - 1, 2, 3, ...
     * @returns {string} 'DD.MM.YYYY' или ''
     */
    function calculateActivityDate(tourStart, tourDay) {
        if (!tourStart || !tourDay || tourDay < 1) return '';
        const parts = String(tourStart).split('-').map(p => parseInt(p, 10));
        if (parts.length !== 3 || parts.some(isNaN)) return '';
        const [year, month, day] = parts;
        const start = new Date(year, month - 1, day);
        if (isNaN(start.getTime())) return '';
        const activity = new Date(start);
        activity.setDate(activity.getDate() + (tourDay - 1));
        return activity.toLocaleDateString('ru-RU', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    }

    /**
     * Группировка услуг по дню тура.
     * Услуги без day → группа { day: 0, label: 'Без дня' }
     * @param {Array} services
     * @returns {Array<{day: number, label: string, services: Array}>}
     */
    function groupByDay(services) {
        const map = new Map();
        for (const s of services || []) {
            const day = s.day || 0;
            if (!map.has(day)) {
                map.set(day, {
                    day,
                    label: day === 0 ? 'Без дня' : `День ${day}`,
                    services: []
                });
            }
            map.get(day).services.push(s);
        }
        return Array.from(map.values()).sort((a, b) => {
            if (a.day === 0) return 1;   // "Без дня" в конце
            if (b.day === 0) return -1;
            return a.day - b.day;
        });
    }

    /**
     * Группировка услуг по поставщику (contractor).
     * Услуги без contractor → группа 'Без поставщика'.
     * @param {Array} services
     * @returns {Array<{contractor: string, services: Array}>}
     */
    function groupBySupplier(services) {
        const map = new Map();
        for (const s of services || []) {
            const contractor = (s.contractor || '').trim() || 'Без поставщика';
            if (!map.has(contractor)) {
                map.set(contractor, { contractor, services: [] });
            }
            map.get(contractor).services.push(s);
        }
        // Сортируем алфавитно, "Без поставщика" в конец
        return Array.from(map.values()).sort((a, b) => {
            if (a.contractor === 'Без поставщика') return 1;
            if (b.contractor === 'Без поставщика') return -1;
            return a.contractor.localeCompare(b.contractor, 'ru');
        });
    }

    /**
     * Форматирование числа в локализованной форме (ru-RU).
     * @param {number} n
     * @param {object} [opts] - {decimals=0, currency='', useGrouping=true}
     */
    function formatNumber(n, opts = {}) {
        const { decimals = 0, currency = '', useGrouping = true } = opts;
        const num = Number(n);
        if (isNaN(num)) return '';
        const formatted = num.toLocaleString('ru-RU', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            useGrouping
        });
        return currency ? `${formatted} ${currency}` : formatted;
    }

    const FormatUtils = {
        calculateActivityDate,
        groupByDay,
        groupBySupplier,
        formatNumber
    };

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = FormatUtils;
    }
    if (global) {
        global.FormatUtils = FormatUtils;
    }
})(typeof window !== 'undefined' ? window : globalThis);
